import React from "react";
import {
  Bold,
  Italic,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Minus,
  CornerDownLeft,
} from "lucide-react";

/**
 * Small markdown toolbar. Wraps the current selection in the chosen syntax,
 * or inserts a snippet at the caret if nothing is selected.
 */
export default function MarkdownToolbar({ textareaRef, value, onChange }) {
  const apply = (kind) => {
    const el = textareaRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const before = value.slice(0, start);
    const sel = value.slice(start, end);
    const after = value.slice(end);

    let inserted = "";
    let nextStart = start;
    let nextEnd = end;

    const wrap = (token) => {
      inserted = `${token}${sel || "text"}${token}`;
      nextStart = start + token.length;
      nextEnd = nextStart + (sel ? sel.length : 4);
    };
    const linePrefix = (prefix) => {
      const block = sel || "Heading";
      inserted = `${prefix}${block}`;
      nextStart = start + prefix.length;
      nextEnd = nextStart + block.length;
    };
    const listPrefix = (prefix) => {
      const lines = (sel || "First item\nSecond item").split("\n");
      inserted = lines.map((l) => `${prefix}${l}`).join("\n");
      nextStart = start;
      nextEnd = start + inserted.length;
    };

    const needsLeadingNewlines =
      before.length > 0 && !before.endsWith("\n\n") && before.endsWith("\n")
        ? "\n"
        : before.length > 0 && !before.endsWith("\n\n")
        ? "\n\n"
        : "";

    switch (kind) {
      case "bold":
        wrap("**");
        break;
      case "italic":
        wrap("*");
        break;
      case "h1":
        inserted = `${needsLeadingNewlines}# ${sel || "Heading"}`;
        nextStart = start + needsLeadingNewlines.length + 2;
        nextEnd = nextStart + (sel ? sel.length : 7);
        break;
      case "h2":
        inserted = `${needsLeadingNewlines}## ${sel || "Heading"}`;
        nextStart = start + needsLeadingNewlines.length + 3;
        nextEnd = nextStart + (sel ? sel.length : 7);
        break;
      case "h3":
        inserted = `${needsLeadingNewlines}### ${sel || "Heading"}`;
        nextStart = start + needsLeadingNewlines.length + 4;
        nextEnd = nextStart + (sel ? sel.length : 7);
        break;
      case "ul":
        listPrefix("- ");
        break;
      case "ol":
        listPrefix("1. ");
        break;
      case "quote":
        inserted = `${needsLeadingNewlines}> ${sel || "A quiet reflection."}`;
        nextStart = start + needsLeadingNewlines.length + 2;
        nextEnd = nextStart + (sel ? sel.length : 20);
        break;
      case "hr":
        inserted = `${needsLeadingNewlines}---\n\n`;
        nextStart = start + inserted.length;
        nextEnd = nextStart;
        break;
      case "br":
        inserted = "  \n";
        nextStart = start + inserted.length;
        nextEnd = nextStart;
        break;
      default:
        return;
    }

    const next = before + inserted + after;
    onChange(next);

    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(nextStart, nextEnd);
    });
  };

  const Btn = ({ icon: Icon, label, k }) => (
    <button
      type="button"
      onClick={() => apply(k)}
      title={label}
      aria-label={label}
      data-testid={`md-${k}`}
      className="inline-flex items-center justify-center w-9 h-9 rounded-lg text-foreground/70 hover:text-foreground hover:bg-secondary transition-colors"
    >
      <Icon size={15} strokeWidth={1.5} />
    </button>
  );

  return (
    <div
      data-testid="markdown-toolbar"
      className="flex flex-wrap items-center gap-1 mt-2 mb-3 p-1.5 border border-border rounded-2xl bg-secondary/40"
    >
      <Btn icon={Bold} label="Bold" k="bold" />
      <Btn icon={Italic} label="Italic" k="italic" />
      <span className="w-px h-5 bg-border mx-1" />
      <Btn icon={Heading1} label="Heading 1" k="h1" />
      <Btn icon={Heading2} label="Heading 2" k="h2" />
      <Btn icon={Heading3} label="Heading 3" k="h3" />
      <span className="w-px h-5 bg-border mx-1" />
      <Btn icon={List} label="Bulleted list" k="ul" />
      <Btn icon={ListOrdered} label="Numbered list" k="ol" />
      <Btn icon={Quote} label="Pull quote" k="quote" />
      <span className="w-px h-5 bg-border mx-1" />
      <Btn icon={Minus} label="Horizontal divider" k="hr" />
      <Btn icon={CornerDownLeft} label="Line break" k="br" />
    </div>
  );
}
